<template>
  <v-card elevation="0" color="transparent" height="100%">
    <v-fade-transition leave-absolute>
      <v-skeleton-loader v-if="!bio" type="card-avatar, article, list-item-three-line, actions"/>
      <v-card
        :id="`section-0`"
        elevation="0"
        :color="bio.backgroundColor"
        v-else
        min-height="100%"
        :class="`bio-shell ${getFontClass()} ${['xs' , 'sm'].includes($vuetify.breakpoint.name) ? 'rounded-0' : getShape().borderRadius} ${forceMobile ? 'bio-force-mobile' : ''}`"
      >
        <div v-if="bio.onlineAddress.code" class="bio-sticky-header-anchor">
          <div class="bio-sticky-header" :class="{'bio-sticky-header-visible': stickyVisible}" :style="`background: ${bio.headerColor}`">
            <v-avatar size="34" ref="stickyAvatar" :color="!bio.picture || bio.picture.deleted || isImageBroken(bio.picture.src) ? '#E5E9F0' : undefined">
              <v-img v-if="bio.picture && !bio.picture.deleted && !isImageBroken(bio.picture.src)" :src="bio.picture.src" :alt="bio.name" :transition="false"/>
              <v-icon v-else size="18" color="#9AA5B1">mdi-account</v-icon>
            </v-avatar>
            <div class="bio-sticky-text" ref="stickyText">
              <span class="bio-sticky-username">{{ bio.name }}</span>
              <span class="bio-sticky-name">{{ stickyDescriptionSnippet }}</span>
            </div>
            <v-spacer/>
            <button type="button" class="bio-share-fab bio-sticky-share" @click="openShare" :aria-label="$t('share')">
              <v-icon size="18" color="primary">mdi-share-variant</v-icon>
            </button>
          </div>
        </div>

        <div class="bio-shell-scroll">
        <div class="bio-cover-col" :class="{'bio-cover-col-empty': !hasCover}" :style="`background-color: ${bio.headerColor}`">
          <v-img
            v-if="hasCover"
            :id="`section-cover`"
            :src="bio.cover.src"
            :alt="bio.name ? `Capa de ${bio.name}` : ''"
            class="bio-cover-image"
            eager
            :transition="false"
            @error="markImageBroken(bio.cover && bio.cover.src)"
          />
          <div class="bio-cover-gradient-vertical" :style="`background: linear-gradient(to bottom, transparent 0%, ${bio.backgroundColor} 100%)`"/>
          <div class="bio-cover-gradient-horizontal" :style="`background: linear-gradient(to left, transparent 0%, ${bio.backgroundColor} 100%)`"/>
        </div>

        <button
          type="button"
          class="bio-share-btn"
          v-if="bio.onlineAddress.code && !stickyVisible"
          :id="`section-share`"
          @click="openShare"
          :aria-label="$t('share')"
        >
          <v-icon size="20" color="primary">mdi-share-variant</v-icon>
        </button>

        <div class="bio-content-col" :class="{'bio-content-col-with-primary': !!primaryLinkItem}">
          <div :id="`section-2`" />
          <div class="bio-avatar-wrap" ref="avatarWrap">
            <v-avatar size="96" ref="profileAvatar" class="bio-avatar" :color="!bio.picture || bio.picture.deleted || isImageBroken(bio.picture.src) ? '#E5E9F0' : undefined">
              <v-img v-if="bio.picture && !bio.picture.deleted && !isImageBroken(bio.picture.src)" :id="`section-picture`" :src="bio.picture.src" :alt="bio.name" eager :transition="false" @error="markImageBroken(bio.picture.src)"/>
              <v-icon v-else size="48" color="#9AA5B1">mdi-account</v-icon>
            </v-avatar>
          </div>

          <h1 class="bio-name" :id="`section-name`" ref="nameEl" :style="`color: ${bio.titleColor}`">{{ bio.name }}</h1>
          <p v-if="bio.description" class="bio-description" :id="`section-description`" ref="descriptionEl" :style="`color: ${bio.descriptionColor}`">{{ bio.description }}</p>

          <div class="bio-metadata" ref="metadataEl" v-if="bio.headline || bio.locationStatus">
            <div class="bio-metadata-row" v-if="bio.headline" :style="`color: ${bio.descriptionColor}`">
              <v-icon size="16" :color="bio.descriptionColor">{{ bio.headlineIcon || 'mdi-briefcase-outline' }}</v-icon>
              <span>{{ bio.headline }}</span>
            </div>
            <div class="bio-metadata-row" v-if="bio.locationStatus" :style="`color: ${bio.descriptionColor}`">
              <v-icon size="16" :color="bio.descriptionColor">{{ bio.locationStatusIcon || 'mdi-map-marker-outline' }}</v-icon>
              <span>{{ bio.locationStatus }}</span>
            </div>
          </div>

          <div class="bio-social-row" v-if="socialLinksVisible.length && bio.placementSocialLinksOnTop">
            <BioSocialLinkCard
              v-for="(link, index) in socialLinksTop"
              :key="getItemKey(link)"
              :link="link"
              :index="index"
              :iconColor="bio.socialIconsColor"
              :titleColor="bio.titleColor"
              :descriptionColor="bio.descriptionColor"
              :forceMobile="forceMobile"
              @open="goTo(link.onlineAddress)"
            />
            <BioSocialLinksGrid
              v-if="socialLinksOverflow.length"
              :links="socialLinksVisible"
              :visibleCount="socialLinksVisibleCount"
              :iconColor="bio.socialIconsColor"
              :descriptionColor="bio.descriptionColor"
              :backgroundColor="bio.backgroundColor"
              :titleColor="bio.titleColor"
              :sectionColor="bio.sectionColor"
              :sectionTextColor="bio.sectionTextColor"
              :getItemKey="getItemKey"
              @open="goTo"
            />
          </div>

          <div :id="`section-1`" />
          <div class="bio-content-links">
            <BioContentLinkRenderer
              v-for="(item, index) in contentLinks"
              :key="getItemKey(item)"
              :id="`section-link-${index}`"
              :item="item"
              :index="index"
              :shapeClass="getShape().borderRadius"
              :outlined="getShape().outlined"
              :shadowClass="getShadow()"
              :sectionColor="bio.sectionColor"
              :sectionTextColor="bio.sectionTextColor"
              :backgroundColor="bio.backgroundColor"
              :destinationLabel="isDuplicateTitle(item) ? getDestinationLabel(item.onlineAddress) : null"
              :isImageBroken="isImageBroken"
              :markImageBroken="markImageBroken"
              :convertToEmbedUrl="convertToEmbedUrl"
              class="bio-content-link-item"
              @open="goTo(item.onlineAddress)"
            />
          </div>

          <div class="bio-social-row" v-if="socialLinksVisible.length && !bio.placementSocialLinksOnTop">
            <BioSocialLinkCard
              v-for="(link, index) in socialLinksTop"
              :key="getItemKey(link)"
              :link="link"
              :index="index"
              :iconColor="bio.socialIconsColor"
              :titleColor="bio.titleColor"
              :descriptionColor="bio.descriptionColor"
              :forceMobile="forceMobile"
              @open="goTo(link.onlineAddress)"
            />
            <BioSocialLinksGrid
              v-if="socialLinksOverflow.length"
              :links="socialLinksVisible"
              :visibleCount="socialLinksVisibleCount"
              :iconColor="bio.socialIconsColor"
              :descriptionColor="bio.descriptionColor"
              :backgroundColor="bio.backgroundColor"
              :titleColor="bio.titleColor"
              :sectionColor="bio.sectionColor"
              :sectionTextColor="bio.sectionTextColor"
              :getItemKey="getItemKey"
              @open="goTo"
            />
          </div>

          <div class="bio-brand-cta" ref="brandCta" :style="{'--bio-cta-bg': bio.backgroundColor}">
            <div class="bio-brand-cta-avatars">
              <v-avatar size="60" class="bio-brand-cta-avatar-profile">
                <v-img v-if="bio.picture && !bio.picture.deleted && !isImageBroken(bio.picture.src)" :src="bio.picture.src" :alt="bio.name" eager :transition="false"/>
                <v-icon v-else size="30" color="#9AA5B1">mdi-account</v-icon>
              </v-avatar>
              <span class="bio-brand-cta-avatar-logo">
                <img src="/images/logo/logo-02.png" alt="migly" width="26" height="26"/>
              </span>
            </div>
            <a href="/" target="_blank" class="bio-brand-cta-title bio-tactile" :style="`color: ${bio.descriptionColor}`">{{ $t('bioBrandCtaTitle') }}</a>
            <div class="bio-brand-cta-links" :style="`color: ${bio.descriptionColor}`">
              <a href="/" target="_blank" class="bio-brand-cta-link">{{ $t('bioBrandHome') }}</a>
              <span class="bio-brand-cta-sep">|</span>
              <a href="/privacy-policy" target="_blank" class="bio-brand-cta-link">{{ $t('bioBrandPrivacyPolicy') }}</a>
              <span class="bio-brand-cta-sep">|</span>
              <a href="/#planos" target="_blank" class="bio-brand-cta-link">{{ $t('bioBrandPlans') }}</a>
            </div>
          </div>
        </div>
        </div>

        <BioPrimaryLink :item="primaryLinkItem" :backgroundColor="bio.sectionColor" :textColor="bio.sectionTextColor" :isImageBroken="isImageBroken" :markImageBroken="markImageBroken" :forceMobile="forceMobile" @open="goTo(primaryLinkItem.onlineAddress)"/>

        <transition name="bio-share-overlay">
          <div v-if="shareOpen" class="bio-share-overlay" @click.self="closeShare">
            <div class="bio-share-overlay-panel">
              <ShareOptionsPanel :onlineAddress="bio.onlineAddress" @close="closeShare"/>
            </div>
          </div>
        </transition>
      </v-card>
    </v-fade-transition>
  </v-card>
</template>
<script>
import { apiV1OpenGraph } from "../../../service/resources/api-v1-open-graph"
import ShareOptionsPanel from './ShareOptionsPanel.vue';
import BioContentLinkRenderer from './BioContentLinkRenderer.vue';
import BioPrimaryLink from './BioPrimaryLink.vue';
import BioSocialLinksGrid from './BioSocialLinksGrid.vue';
import BioSocialLinkCard from './BioSocialLinkCard.vue';
import { loadBioFonts } from '../../../service/bio-fonts';
import { gsap } from 'gsap';
  export default {
    name: 'BiographySmartphoneView',
    props: {
      bio: { type: Object, default: null },
      // Forces the mobile/vertical composition regardless of the actual
      // viewport width - purely for the editor's preview panel (see
      // PainelBioPage.vue), so it can show "what this looks like on a
      // phone" even though the editor itself is a wide desktop window.
      // The real public bio page never passes this (defaults to false),
      // so its own responsive behavior is completely unchanged - this
      // only ever suppresses the desktop @media rules below via the
      // .bio-force-mobile class, never replaces or duplicates them.
      forceMobile: { type: Boolean, default: false },
    },
    components: {
      ShareOptionsPanel,
      BioContentLinkRenderer,
      BioPrimaryLink,
      BioSocialLinksGrid,
      BioSocialLinkCard,
    },
    data: () => ({
      loading: true,
      host: null,
      brokenImageSrcs: {},
      stickyVisible: false,
      avatarEl: null,
      stickyObserver: null,
      shareOpen: false,
    }),
    watch: {
      'bio': function() {
        setTimeout(() => {
          this.setupStickyHeaderWatcher()
        }, 1)
      },
      stickyVisible: function(visible) {
        if (visible) this.playStickyEntrance()
      },
    },
    mounted: function() {
      loadBioFonts()
      setTimeout(() => {
        this.loading = false
      }, 1000);
      this.host = `${window.location.protocol}//${window.location.host.replace('www.', '')}`;
      this.setupStickyHeaderWatcher()
      this.playHeroEntrance()
    },
    beforeDestroy: function() {
      this.teardownStickyHeaderWatcher()
    },
    computed: {
      hasCover: function() {
        return !!(this.bio && this.bio.cover && !this.bio.cover.deleted && this.bio.cover.src && !this.isImageBroken(this.bio.cover.src))
      },
      duplicateTitles: function() {
        const counts = {}
        if(!this.bio || !this.bio.biographyHasOnlineAddresses)
          return counts
        this.bio.biographyHasOnlineAddresses.filter(v => !v.deleted && v.title).forEach(v => {
          const key = v.title.trim()
          counts[key] = (counts[key] || 0) + 1
        })
        return counts
      },
      // Primary Link is excluded from the content list and rendered on its
      // own by BioPrimaryLink (spec: "Primary Link is a distinct,
      // always-visible element").
      primaryLinkItem: function() {
        return this.bio.biographyHasOnlineAddresses.filter(v => !v.deleted && v.primaryLink)[0] || null
      },
      contentLinks: function() {
        return this.bio.biographyHasOnlineAddresses.filter(v => !v.deleted && !v.primaryLink)
      },
      socialLinksVisible: function() {
        return this.bio.socialLinks.filter(v => !v.deleted)
      },
      // Only the top few render as inline cards; the rest are reachable
      // through the overflow grid ("ver todos"), so a long social list
      // doesn't crowd the header.
      socialLinksVisibleCount: function() {
        return 3
      },
      socialLinksTop: function() {
        return this.socialLinksVisible.slice(0, this.socialLinksVisibleCount)
      },
      socialLinksOverflow: function() {
        return this.socialLinksVisible.slice(this.socialLinksVisibleCount)
      },
      stickyDescriptionSnippet: function() {
        if(!this.bio.description)
          return `@${this.bio.onlineAddress.code}`
        return this.bio.description.length > 60 ? `${this.bio.description.slice(0, 60)}…` : this.bio.description
      },
    },
    methods: {
      openShare: function() {
        this.shareOpen = true
      },
      closeShare: function() {
        this.shareOpen = false
      },
      isDuplicateTitle: function(item) {
        return !!item.title && this.duplicateTitles[item.title.trim()] > 1
      },
      getDestinationLabel: function(address) {
        if(!address || !address.url)
          return ''
        try {
          return new URL(address.url).hostname.replace(/^www\./, '')
        } catch(e) {
          return address.url
        }
      },
      getFontClass: function () {
        return [
                      {id: 1, font: 'roboto'},
                      {id: 2, font: 'open-sans'},
                      {id: 3, font: 'lora'},
                      {id: 4, font: 'montserrat'},
                      {id: 5, font: 'merriweather'},
                      {id: 6, font: 'raleway'},
                      {id: 7, font: 'noto-serif-oriya'},
                      {id: 8, font: 'architects-daughter'},
                      {id: 9, font: 'permanent-marker-regular'},
                      ].filter(v => v.id == this.bio.font)[0].font
      },
      getShape: function() {
        return [
          {id: 1, borderRadius: 'rounded-0', outlined: true},
          {id: 2, borderRadius: 'rounded-sm', outlined: true},
          {id: 3, borderRadius: 'rounded', outlined: true},
          {id: 5, borderRadius: 'rounded-xl', outlined: true},
          {id: 6, borderRadius: 'rounded-0', outlined: false},
          {id: 7, borderRadius: 'rounded-sm', outlined: false},
          {id: 8, borderRadius: 'rounded', outlined: false},
          {id: 9, borderRadius: 'rounded-xl', outlined: false},
        ].filter(v => v.id == this.bio.shape)[0]
      },
      getShadow: function() {
        return [
          {id: 1, shadow: 'sombra-leve'},
          {id: 2, shadow: 'sombra-interna'},
          {id: 3, shadow: 'sombra-difusa'},
          {id: 4, shadow: 'sombra-intensa'},
        ].filter(v => v.id == this.bio.shadow)[0].shadow
      },
      getItemKey: function(item) {
        return item.id || item._localKey
      },
      isImageBroken: function(src) {
        return !!src && !!this.brokenImageSrcs[src]
      },
      markImageBroken: function(src) {
        if(src)
          this.$set(this.brokenImageSrcs, src, true)
      },
      teardownStickyHeaderWatcher: function() {
        if(this.stickyObserver)
          this.stickyObserver.disconnect()
        this.stickyObserver = null
        this.avatarEl = null
      },
      // Was previously a manual scroll-listener that walked up the DOM
      // looking for "the scrolling ancestor" (checking overflow-y +
      // scrollHeight>clientHeight) - fragile in practice: on the public
      // bio page the real scrolling container is App.vue's global
      // .full-height (height:100vh; overflow-x:clip; overflow-y:auto),
      // and that overflow-x:clip pairing made its own scrollHeight not
      // reflect the (genuinely overflowing, verified via getBoundingClientRect)
      // content in testing, so the old code's scroll listener never fired
      // and the bar never appeared. IntersectionObserver sidesteps the
      // whole "which ancestor scrolls" question entirely - root:null
      // tracks the avatar's actual on-screen position against the true
      // viewport regardless of which ancestor (App.vue's global wrapper on
      // mobile, or .bio-content-col's own internal scroll on desktop)
      // is the one moving it.
      setupStickyHeaderWatcher: function() {
        this.teardownStickyHeaderWatcher()
        this.$nextTick(() => {
          const target = this.$refs.profileAvatar && this.$refs.profileAvatar.$el ? this.$refs.profileAvatar.$el : null
          if(!target)
            return
          this.avatarEl = target
          this.stickyObserver = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1]
            // Only count it as "scrolled past" when it left via the top
            // (boundingClientRect.top < 0) - leaving via the bottom (not
            // yet scrolled to) shouldn't show the bar.
            this.stickyVisible = !entry.isIntersecting && entry.boundingClientRect.top < 0
          }, {threshold: 0})
          this.stickyObserver.observe(this.avatarEl)
        })
      },
      // Entrance for the profile hero (avatar, name, description, headline/
      // status) - the very top of the page, per feedback ("animações para...
      // topo do site"). Runs once on mount, not scroll-triggered - unlike
      // the content/social link cards below the fold, this is always the
      // first thing on screen.
      playHeroEntrance: function() {
        const avatar = this.$refs.avatarWrap
        const name = this.$refs.nameEl
        const description = this.$refs.descriptionEl
        const metadata = this.$refs.metadataEl
        const targets = [avatar, name, description, metadata].filter(Boolean)
        if (!targets.length) return
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          gsap.set(targets, { opacity: 1, y: 0, scale: 1 })
          return
        }
        gsap.set(avatar, { opacity: 0, scale: 0.6 })
        gsap.set([name, description, metadata].filter(Boolean), { opacity: 0, y: 14 })
        const tl = gsap.timeline({ delay: 0.1 })
        if (avatar) tl.to(avatar, { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.8)', clearProps: 'transform' })
        if (name) tl.to(name, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', clearProps: 'transform' }, '-=0.25')
        if (description) tl.to(description, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', clearProps: 'transform' }, '-=0.25')
        if (metadata) tl.to(metadata, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', clearProps: 'transform' }, '-=0.25')
      },
      // Mirrors playHeroEntrance's avatar-pop-then-text-fade choreography
      // for the sticky bar specifically, so the photo/name pairing reads as
      // the same "arrival" moment every time the bar appears - not just the
      // whole bar's own CSS opacity/slide (see .bio-sticky-header-visible),
      // which still handles the bar's own show/hide independently of this.
      playStickyEntrance: function() {
        const avatar = this.$refs.stickyAvatar && this.$refs.stickyAvatar.$el
        const text = this.$refs.stickyText
        const targets = [avatar, text].filter(Boolean)
        if (!targets.length) return
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          gsap.set(targets, { opacity: 1, x: 0, scale: 1 })
          return
        }
        gsap.killTweensOf(targets)
        gsap.set(avatar, { opacity: 0, scale: 0.5 })
        gsap.set(text, { opacity: 0, x: -8 })
        const tl = gsap.timeline()
        if (avatar) tl.to(avatar, { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(1.8)', clearProps: 'transform' })
        if (text) tl.to(text, { opacity: 1, x: 0, duration: 0.3, ease: 'power2.out', clearProps: 'transform' }, '-=0.22')
      },
      loadOpenGraph: async function(item) {
        item.loading = true
        this.prepareOG(item)
        this.$forceUpdate()
        try {

          const response = (await apiV1OpenGraph.tags({source: item.onlineAddress.url})).data;

          item.onlineAddress.openGraph.openGraphFields.forEach(v => {v.deleted = true, v.update = true})
          for (const key in response) {
            if(item.onlineAddress.openGraph.openGraphFields.filter(v => v.name == key)[0]) {
              item.onlineAddress.openGraph.openGraphFields.filter(v => v.name == key)[0].value = response[key]
              item.onlineAddress.openGraph.openGraphFields.filter(v => v.name == key)[0].deleted = false
              item.onlineAddress.openGraph.openGraphFields.filter(v => v.name == key)[0].update = true
            } else {
              item.onlineAddress.openGraph.openGraphFields.push({name: key, value: response[key], deleted: false, update: true})
            }
          }
          item.loading = false
          this.$forceUpdate()
        } catch (error) {
          this.$commons.updateSnackbar({show: true, color: 'error', text: this.$t('errorLinkLoad')})
          item.loading = false
          this.$forceUpdate()
        }
      },
      prepareOG: function(item) {
        if(!item.onlineAddress.openGraph)
          item.onlineAddress.openGraph = {openGraphFields: []}
        if(!item.onlineAddress.openGraph.openGraphFields)
          item.onlineAddress.openGraph.openGraphFields = []
        item.onlineAddress.openGraph.onlineAddress = {id: item.onlineAddress.id}
      },
      goTo(address) {
        // Always route through the link's own code (Redirect.vue) - every bio link's
        // OnlineAddress already gets one on save, and this is what puts the visit through
        // the url-security-verification gate. A direct window.open(address.url) fallback
        // would bypass that gate entirely.
        if(address.code) {
          let route = this.$router.resolve({ path: `/${address.code}` });
          window.open(route.href);
        }
      },
      convertToEmbedUrl: function(youtubeUrl) {
        // Verifica se a URL é válida
        const urlPattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
        if (!youtubeUrl.match(urlPattern)) {
            return null;
        }

        // Extrai o ID do vídeo da URL
        const videoIdPattern = /(?:youtube\.com\/(?:[^\\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
        const match = youtubeUrl.match(videoIdPattern);

        if (match && match[1]) {
            // Strips as much of the player chrome as the embed API allows:
            // controls=0 (hides the scrubber/play bar), fs=0 (fullscreen
            // button), disablekb=1 (keyboard shortcuts), rel=0 (related
            // videos on end screen), modestbranding=1 (YouTube logo),
            // iv_load_policy=3 (annotations). Per feedback ("remover todos
            // os controles possíveis").
            return `https://www.youtube.com/embed/${match[1]}?controls=0&fs=0&disablekb=1&rel=0&modestbranding=1&iv_load_policy=3`;
        }

        return null;
      },
    },
  }
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@500&display=swap');

/* Responsive dual composition: vertical column below 1024px (phones AND
   tablets), horizontal profile/cover card at 1024px and above — a custom
   breakpoint, not a Vuetify one, since this component already mixes
   ['xs','sm'] groupings elsewhere. */
.bio-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
/* .bio-shell-scroll wraps .bio-cover-col/.bio-share-btn/.bio-content-col
   purely so forceMobile (below) can scroll them as one unit while
   .bio-primary-link-wrap/.bio-sticky-header-anchor stay outside it (see
   that rule's own comment). display:contents makes the wrapper invisible
   to layout by default, so .bio-cover-col/.bio-content-col remain DIRECT
   .bio-shell flex children everywhere else - critical for the real public
   page's desktop composition just below, which uses flex `order` on
   those two to swap cover/content left-right; `order` only affects direct
   flex children, so without display:contents here the desktop cover image
   silently stopped being a flex item at all and rendered stacked on top
   like the mobile layout instead of split to the right. */
.bio-shell-scroll {
  display: contents;
}
/* forceMobile (editor preview): .bio-shell is bounded to its container's
   height instead of growing to content height, and only
   .bio-shell-scroll (cover+content) scrolls internally - .bio-primary-link-wrap
   and .bio-sticky-header-anchor stay OUTSIDE that scrolling wrapper as
   direct .bio-shell children, so switching them to position:absolute
   (see their own force-mobile rules) anchors them to this stable,
   non-scrolling box instead of scrolling away with the content beneath
   them. A position:fixed+transform "trap" was tried first and doesn't
   work here: a fixed element nested INSIDE the element that actually
   scrolls still scrolls with its content in every browser tested,
   regardless of which ancestor's transform defines its containing block
   - it has to be a sibling of the scrolling wrapper, not a descendant of
   it, exactly like the existing desktop (:not(.bio-force-mobile)) layout
   below already does with .bio-content-col. */
.bio-shell.bio-force-mobile {
  height: 100%;
}
.bio-shell.bio-force-mobile .bio-shell-scroll {
  display: block;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
.bio-shell.bio-force-mobile .bio-shell-scroll::-webkit-scrollbar {
  width: 0px;
}
.bio-cover-col {
  position: relative;
  height: 34vw;
  max-height: 260px;
  min-height: 120px;
  overflow: hidden;
}
.bio-cover-col-empty {
  min-height: 120px;
  max-height: 120px;
}
.bio-cover-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.bio-cover-image ::v-deep .v-image__image {
  background-size: cover;
}
.bio-cover-gradient-vertical {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 60%;
  pointer-events: none;
}
.bio-cover-gradient-horizontal {
  display: none;
}
.bio-content-col {
  position: relative;
  padding: 0 1rem 1.25rem;
}
.bio-avatar-wrap {
  display: flex;
  justify-content: center;
  margin-top: -48px;
  margin-bottom: 0.5rem;
}
.bio-avatar {
  border: 3px solid #FFFFFF;
  box-shadow: 0 6px 16px rgba(16,24,39,0.18);
}
.bio-name {
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin: 0 0 0.15rem;
  overflow-wrap: break-word;
  word-break: break-word;
}
.bio-description {
  font-size: 0.95rem;
  font-weight: 500;
  text-align: center;
  margin: 0 0 0.5rem;
  overflow-wrap: break-word;
  word-break: break-word;
}
.bio-metadata {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.85rem;
}
.bio-metadata-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 400;
  /* Subtle (not stark) step down from .bio-description, so name >
     description > headline/status reads as one graduated hierarchy
     instead of three same-weight lines. */
  opacity: 0.8;
}
.bio-social-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 0.6rem;
  /* Equal top/bottom regardless of placement (top-of-profile or
     bottom-of-links) - previously only one side had margin depending on
     .bio-social-row-bottom, so the row read as tight against whichever
     neighbor was on its unmargined side. */
  margin: 1.75rem 0;
}
.bio-content-links {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.bio-content-col-with-primary {
  padding-bottom: 90px;
}
.bio-brand-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.9rem;
  margin-top: 2rem;
  /* A neutral tinted card, not just a hairline above the last link - a
     border-top read as an afterthought/fine-print footer. rgba(128,128,128)
     stays theme-agnostic (no bio color prop needed) and holds up against
     both light and dark bio backgrounds, per feedback ("dar mais destaque
     ao rodapé de marca"). */
  padding: 1.75rem 1.25rem;
  border-radius: 1.5rem;
  background: rgba(128, 128, 128, 0.06);
  box-shadow: inset 0 0 0 1px rgba(128, 128, 128, 0.12);
}
.bio-brand-cta-avatars {
  position: relative;
  display: flex;
  align-items: center;
  height: 60px;
  /* The logo badge overlaps the profile photo by design (see the two
     avatar sizes below) - narrower than the sum of both widths (60+44)
     on purpose, so they overlap by the difference instead of just
     touching. Per feedback, this overlap needed to be deeper (28px here,
     was 12px). */
  width: 76px;
}
.bio-brand-cta-avatar-profile {
  position: absolute;
  left: 0;
  border: 3px solid var(--bio-cta-bg, #fff);
}
.bio-brand-cta-avatar-logo {
  position: absolute;
  right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #FFFFFF;
  border: 3px solid var(--bio-cta-bg, #fff);
  box-shadow: 0 4px 10px rgba(16, 24, 39, 0.18);
  overflow: hidden;
}
.bio-brand-cta-avatar-logo img {
  border-radius: 50%;
}
.bio-brand-cta-title {
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: none;
  color: inherit;
  padding: 0.55rem 1.15rem;
  border-radius: 999px;
  background: rgba(128, 128, 128, 0.1);
  transition: background 0.2s ease;
}
@media (hover: hover) {
  .bio-brand-cta-title:hover {
    background: rgba(128, 128, 128, 0.16);
  }
}
.bio-brand-cta-links {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  opacity: 0.75;
}
.bio-brand-cta-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.bio-brand-cta-sep {
  opacity: 0.6;
}

/* 1024px, not 768px: below this (phones AND tablets) the bio stays
   full-width/vertical. Only genuine desktop widths get the horizontal
   card — a tablet showing the cramped side-by-side card looked wrong, per
   feedback ("no celular e tablet ocupa 100%"). BioPrimaryLink.vue and
   Redirect.vue's .bio-page-container override share this same breakpoint;
   keep all three in sync if it changes again. */
@media (min-width: 1024px) {
  /* .bio-sticky-header-anchor sits as a sibling of .bio-cover-col /
     .bio-content-col. In the mobile column layout it stretches full-width
     for free (flex column's default align-items:stretch). Once .bio-shell
     switches to flex-direction:row, "stretch" instead applies to height,
     and the anchor's own width shrinks to near-zero content size - so the
     header inside it (left:0/right:0, resolved against that near-zero
     containing block) rendered squished at the far left instead of
     spanning the card, which is why share ended up nowhere near the right
     edge. Taking it out of the row's flex flow with position:absolute
     lets it overlay the full card width again, same as the mobile case. */
  /* Extra-specific selector (not just ".bio-sticky-header-anchor") on
     purpose: the unconditional base rule for this class (position: sticky)
     is declared later in this same file, so equal specificity would let
     it win the cascade over this media-query override regardless of the
     viewport actually matching desktop. */
  .bio-shell:not(.bio-force-mobile) .bio-sticky-header-anchor {
    position: absolute;
    left: 0;
    right: 0;
  }
  .bio-shell:not(.bio-force-mobile) {
    flex-direction: row;
    max-width: 1000px;
    /* Fixed aspect ratio (close to the reference mockup's ~3:2 card) instead
       of an open-ended min-height: the card becomes a bounded "frame" and
       .bio-content-col scrolls *inside* it, rather than the whole page
       growing taller and scrolling the browser window. The !important is
       load-bearing: the root element also carries Vuetify's min-height="100%"
       prop as an inline style, which otherwise wins over max-height and
       silently defeats this whole "bounded card" approach. */
    aspect-ratio: 3 / 2;
    max-height: 640px;
    min-height: 0 !important;
    /* Horizontal centering only here - the page-level wrapper
       (Redirect.vue's .bio-page-container) now handles vertical centering
       via flex align-items, since this card is a bounded box that no
       longer just grows down from the top of the page. */
    margin: 0 auto;
    border-radius: 28px;
  }
  .bio-shell:not(.bio-force-mobile) .bio-cover-col {
    order: 2;
    flex: 0 0 45%;
    height: auto;
    max-height: none;
  }
  .bio-shell:not(.bio-force-mobile) .bio-cover-col-empty {
    max-height: none;
  }
  .bio-shell:not(.bio-force-mobile) .bio-cover-gradient-vertical {
    display: none;
  }
  .bio-shell:not(.bio-force-mobile) .bio-cover-gradient-horizontal {
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    /* Widened back out per feedback that the blend read as too abrupt at
       22% - the column split (.bio-cover-col/.bio-content-col) is now
       fixed independently at 45/55, so this can grow purely to soften the
       transition without eating into content space the way it did before
       that split existed. */
    width: 100%;
    pointer-events: none;
  }
  .bio-shell:not(.bio-force-mobile) .bio-content-col {
    order: 1;
    flex: 0 0 55%;
    height: 100%;
    padding: 2rem 1.5rem 2rem 2rem;
    display: flex;
    flex-direction: column;
    /* flex-start, not center: when content is taller than the box (and it
       scrolls internally, see overflow-y below), centering pushes the top
       of the content above the container's own scroll-start position -
       Chromium can't scroll to it, so it's clipped and unreachable. */
    justify-content: flex-start;
    overflow-y: auto;
  }
  .bio-shell:not(.bio-force-mobile) .bio-content-col-with-primary {
    padding-bottom: 110px;
  }
  .bio-shell:not(.bio-force-mobile) .bio-avatar-wrap {
    justify-content: flex-start;
    margin-top: 0;
  }
  .bio-shell:not(.bio-force-mobile) .bio-name {
    font-size: 1.75rem;
    text-align: left;
  }
  .bio-shell:not(.bio-force-mobile) .bio-description {
    text-align: left;
  }
  .bio-shell:not(.bio-force-mobile) .bio-metadata {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.3rem 1.1rem;
  }
  .bio-shell:not(.bio-force-mobile) .bio-social-row {
    justify-content: center;
  }
}

.bio-share-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 30;
  width: 52px;
  height: 52px;
  border: none;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  /* Solid, not translucent - a 0.95-alpha white used to wash out against
     the lighter areas of a busy cover photo, per feedback ("fortalecer o
     botão de compartilhar"). */
  background: #FFFFFF;
  box-shadow: 0 6px 18px rgba(16, 24, 39, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
@media (hover: hover) {
  .bio-share-btn:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 10px 22px rgba(16, 24, 39, 0.34);
  }
}
.bio-share-btn:active {
  transform: scale(0.94);
}
@media (prefers-reduced-motion: reduce) {
  .bio-share-btn {
    transition: none;
  }
  .bio-share-btn:hover,
  .bio-share-btn:active {
    transform: none;
  }
}
@media (min-width: 1024px) {
  .bio-shell:not(.bio-force-mobile) .bio-share-btn {
    top: 28px;
    right: 28px;
  }
}
/* Bigger, bolder glyph to match the larger 52px circle - deep because
   v-icon renders its own inner element, out of reach of this scoped
   style otherwise. */
.bio-share-btn ::v-deep .v-icon {
  font-size: 24px !important;
}

.v-card__title {
  display: block;
}

.sombra-leve {
    box-shadow: 0 2px 8px rgba(16, 24, 39, 0.08);
}
.sombra-intensa {
    box-shadow: 0 12px 28px rgba(16, 24, 39, 0.22);
}
.sombra-difusa {
    box-shadow: 0 8px 20px rgba(16, 24, 39, 0.14);
}
.sombra-interna {
    box-shadow: inset 0 3px 8px rgba(16, 24, 39, 0.12);
}
.bio-sticky-header-anchor {
  /* position:fixed, not sticky: the real scrolling ancestor here is
     App.vue's global .full-height (overflow-x:clip + overflow-y:auto) -
     that overflow-x:clip pairing doesn't reliably register as a sticky
     positioning context in practice (confirmed live: the bar rendered far
     off-screen, stuck at its original pre-scroll position instead of
     pinning to the viewport top). Fixed sidesteps needing a scrolling
     context at all, same reasoning BioPrimaryLink.vue already uses fixed
     for on mobile. Showing/hiding is handled separately by the
     stickyVisible class toggle on the header inside, not by this anchor's
     own positioning. */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 0;
  z-index: 998;
}
/* forceMobile: this anchor is a direct .bio-shell child, outside
   .bio-shell-scroll (see that wrapper's own comment) - position:absolute
   anchors it to .bio-shell's own (now-bounded, non-scrolling) box instead
   of scrolling away with .bio-shell-scroll's content. */
.bio-shell.bio-force-mobile .bio-sticky-header-anchor {
  position: absolute;
}
.bio-sticky-header {
  position: absolute;
  /* A floating card - inset from the edges on all three open sides,
     rounded like every other surface on the page (the share button, the
     footer card, the share dialog) - rather than a bar glued edge-to-edge
     across the full width, per feedback ("pode seguir o padrão de card, um
     pequeno espaçamento no topo e laterais"). */
  top: 10px;
  left: 10px;
  right: 10px;
  border-radius: 1.25rem;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 8px 16px;
  opacity: 0;
  transform: translateY(-16px);
  transition: opacity 0.25s ease, transform 0.25s ease;
  pointer-events: none;
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 10px 24px rgba(16, 24, 39, 0.14);
}
.bio-sticky-header-visible {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}
.bio-sticky-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}
.bio-sticky-username,
.bio-sticky-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 50vw;
}
.bio-sticky-username {
  font-weight: 700;
  font-size: 0.85rem;
  /* Fixed neutral ink, not bio.sectionTextColor - same reasoning as the
     background above: this bar is app chrome on a fixed light surface now,
     not a themed element, so it needs a color guaranteed to read against
     that surface regardless of the bio's own palette. */
  color: #101827;
}
.bio-sticky-name {
  font-size: 0.72rem;
  color: #5B6472;
}
.bio-sticky-share {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
}
.bio-share-fab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  background: #FFFFFF;
  box-shadow: 0 4px 12px rgba(16, 24, 39, 0.18);
  transition: transform 0.2s ease;
}
@media (hover: hover) {
  .bio-share-fab:hover {
    transform: scale(1.08);
  }
}
.bio-share-fab:active {
  transform: scale(0.92);
}
@media (prefers-reduced-motion: reduce) {
  .bio-share-fab {
    transition: none;
  }
  .bio-share-fab:hover,
  .bio-share-fab:active {
    transform: none;
  }
}

/* Share sheet: fixed to the viewport by default (the real public page on
   mobile - there's no bounded "card" there, the page itself is the whole
   screen). Whenever .bio-shell IS a bounded box instead - the desktop
   split-card (min-width:1024px) or the editor's forceMobile phone-mockup
   preview - switched to absolute so the sheet stays inside that box and
   covers exactly its content, per feedback ("o menu precisa ficar dentro
   do container e sobrepor todo o conteúdo") instead of a v-menu popover
   that floated near the trigger button and could clip against the card's
   own overflow:hidden edge. */
.bio-share-overlay {
  position: fixed;
  inset: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(16, 24, 39, 0.55);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}
.bio-shell.bio-force-mobile .bio-share-overlay {
  position: absolute;
}
@media (min-width: 1024px) {
  .bio-shell:not(.bio-force-mobile) .bio-share-overlay {
    position: absolute;
  }
}
.bio-share-overlay-panel {
  width: 100%;
  max-width: 320px;
  /* An explicit height (not max-height) so the .share-card below has a
     definite ancestor to resolve its own percentage max-height against -
     a max-height here wouldn't establish that, and the card's own
     viewport-relative 70vh (see below) would instead go by the full
     browser window instead of the actual space inside this bounded card,
     letting it overflow past .bio-shell's own edge uncroppable/unscrollable
     instead of scrolling in view, per feedback ("o dialog precisa de uma
     rolagem"). */
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* ShareOptionsPanel.vue's own .share-card is a fixed 300px width and a
   viewport-relative 70vh max-height everywhere else (v-menu popovers size
   to their content against the real viewport, so that's what those need) -
   only here, inside this overlay's own bounded wrapper, do both need to
   shrink to the wrapper's actual size instead: width so it doesn't overflow
   narrow viewports, max-height so the card scrolls within the space this
   overlay really has (which on the desktop bounded card is less than 70vh
   of the browser window) rather than clipping invisibly past it. */
.bio-share-overlay-panel ::v-deep .share-card {
  width: 100%;
  max-height: 100%;
}
.bio-share-overlay-enter-active,
.bio-share-overlay-leave-active {
  transition: opacity 0.25s ease;
}
.bio-share-overlay-enter,
.bio-share-overlay-leave-to {
  opacity: 0;
}
.bio-share-overlay-enter-active .bio-share-overlay-panel,
.bio-share-overlay-leave-active .bio-share-overlay-panel {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.bio-share-overlay-enter .bio-share-overlay-panel,
.bio-share-overlay-leave-to .bio-share-overlay-panel {
  transform: translateY(24px) scale(0.96);
}
@media (prefers-reduced-motion: reduce) {
  .bio-share-overlay-enter-active,
  .bio-share-overlay-leave-active,
  .bio-share-overlay-enter-active .bio-share-overlay-panel,
  .bio-share-overlay-leave-active .bio-share-overlay-panel {
    transition: none;
  }
}

.bio-tactile {
  transition: transform 0.15s ease;
}
.bio-tactile:active {
  transform: scale(0.96);
}

@media (prefers-reduced-motion: reduce) {
  .bio-tactile {
    transition: none;
  }
  .bio-tactile:active {
    transform: none;
  }
}
</style>
